import React, { ReactNode, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import { ContentCopy } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import FlightIcon from '@mui/icons-material/Flight';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getBundleStatusInfo } from '~/components/Accommodation/utils/rates';

import { MergedRate, Rate } from '~/services/AccommodationService';

import { RoomRatesWithRates } from './AccommodationPropertyRatesSection';
import CancellationBadge from './CancellationBadge';
import CancellationPolicyModal from './CancellationPolicyModal';
import GroupedRates from './DebugInformation/GroupedRates';
import MealPlanChip from './MealPlanChip';
import RateDetailsModal from './RateDetailsModal';
import RateInclusionsModal from './RateInclusionsModal';
import RateTypeBadge from './RateTypeBadge';
import RoomFacilitiesModal from './RoomFacilitiesModal';
import { getMergedRateKeyForWinningRate } from './mergedRateKeys';
import { formatMargin, formatPrice } from './utils/formatPrice';

const FULL_SCREEN_WIDTH_OVERRIDE_STYLES = {
  position: 'relative',
  left: '50% !important',
  right: '50% !important',
  marginLeft: '-50vw !important',
  marginRight: '-50vw !important',
  width: '100vw !important',
  paddingLeft: '24px !important',
  paddingRight: '24px !important',
  boxSizing: 'border-box !important',
};

export default function AccommodationPropertyRatesResultsTable({
  roomRates,
  openModal,
  groupedMergedRates,
  currency,
  timezone,
}: {
  roomRates: RoomRatesWithRates;
  openModal: (title: string, content: ReactNode) => void;
  groupedMergedRates: Record<string, Array<MergedRate>>;
  currency: string;
  timezone: string;
}) {
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();

  const sortedRoomsWithRates = useMemo(() => {
    return Object.entries(roomRates).sort((a, b) => a[1].sort - b[1].sort);
  }, [roomRates]);

  const handleCancellationPolicyClick = (rate: Rate) => {
    openModal(
      'Cancellation Policy Details',
      <CancellationPolicyModal rate={rate} currency={currency} timezone={timezone} />,
    );
  };

  const handleInclusionsClick = (
    inclusions: Rate['inclusions'],
    bonusInclusions: Rate['inclusions'],
    luxPlusInclusions?: Rate['inclusions'],
  ) => {
    openModal(
      'Rate Inclusions',
      <RateInclusionsModal
        inclusions={inclusions}
        bonusInclusions={bonusInclusions}
        luxPlusInclusions={luxPlusInclusions}
      />,
    );
  };

  const handleFacilitiesClick = (rate: Rate, roomName: string) => {
    openModal('Room Facilities', <RoomFacilitiesModal rate={rate} roomName={roomName} />);
  };

  const handleRateDetailsClick = (rate: Rate) => {
    openModal('Rate Details', <RateDetailsModal rate={rate} currency={currency} />);
  };

  const calculatePropertyFeesTotal = (propertyFees: Array<Rate['propertyFees'][number]> | undefined) => {
    if (!propertyFees || propertyFees.length === 0) {
      return 0;
    }
    return propertyFees.reduce((total, fee) => total + (fee.amount || 0), 0);
  };

  const renderInclusionsBadge = (
    inclusions: Rate['inclusions'],
    bonusInclusions: Rate['inclusions'],
    luxPlusInclusions?: Rate['inclusions'],
  ) => {
    if (
      (!inclusions || inclusions.length === 0) &&
      (!bonusInclusions || bonusInclusions.length === 0) &&
      (!luxPlusInclusions || luxPlusInclusions.length === 0)
    ) {
      return (
        <Button size="small" variant="text" color="primary" disabled>
          None
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant="text"
        color="primary"
        onClick={() => handleInclusionsClick(inclusions, bonusInclusions, luxPlusInclusions)}
        startIcon={<InfoIcon />}
      >
        View
      </Button>
    );
  };

  const handleCopyClick = (id: string) => {
    navigator.clipboard.writeText(id);
    enqueueSnackbar(`Copied to clipboard`, { variant: 'success' });
  };

  // Define columns for DataGrid
  const columns: Array<GridColDef> = [
    {
      field: 'rateType',
      headerName: 'Rate Type',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <RateTypeBadge rate={params.row} />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
    },
    {
      field: 'flights',
      headerName: 'Flights',
      flex: 0.75,
      renderCell: (params) => {
        const bundleStatusInfo = getBundleStatusInfo(params.row.meta);
        return (
          <Box>
            {bundleStatusInfo && (
              <Tooltip title={bundleStatusInfo.description}>
                <Chip icon={<FlightIcon />} size="small" color={bundleStatusInfo.color} sx={{ mt: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'mealPlan',
      headerName: 'Meal Plan',
      flex: 1.4,
      renderCell: (params) => <MealPlanChip boardCode={params.row.boardCode} openModal={openModal} />,
    },
    {
      field: 'cancellation',
      headerName: 'Cancellation',
      flex: 1,
      renderCell: (params) => <CancellationBadge rate={params.row} onClick={handleCancellationPolicyClick} />,
    },
    {
      field: 'inclusions',
      headerName: 'Inclusions',
      flex: 1,
      renderCell: (params) =>
        renderInclusionsBadge(params.row.inclusions, params.row.bonusInclusions, params.row.luxPlusInclusions),
    },
    {
      field: 'bonusInclusions',
      headerName: 'Bonus Inc',
      flex: 1,
      renderCell: (params) =>
        params.row.bonusInclusions.length > 0 ? (
          <Chip icon={<CheckCircleIcon />} size="small" color="success" />
        ) : (
          <Chip icon={<CloseIcon />} size="small" />
        ),
    },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      flex: 1,
      renderCell: (params) => formatPrice(params.row.total?.beforeTaxesFeesPrice as number, currency),
    },
    {
      field: 'taxesAndFees',
      headerName: 'Taxes & Fees',
      flex: 1,
      renderCell: (params) => formatPrice(params.row.total?.taxesAndFees as number, currency),
    },
    {
      field: 'propertyFees',
      headerName: 'Payable at Property',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => {
        const hasPropertyFees = params.row.total?.propertyFees > 0;
        return hasPropertyFees ? formatPrice(params.row.total?.propertyFees as number, currency) : 'None';
      },
    },
    {
      field: 'totalPrice',
      headerName: 'Total Price',
      flex: 1,
      renderCell: (params) => {
        return formatPrice(params.row.total?.displayPrice || 0, currency);
      },
    },
    {
      field: 'luxplusPrice',
      headerName: 'Luxplus Price',
      flex: 1,
      renderCell: (params) => {
        return formatPrice(params.row.luxPlus?.displayPrice || 0, currency);
      },
    },
    {
      field: 'valuedUpTo',
      headerName: 'Valued Up To',
      flex: 1,
      renderCell: (params) => {
        return formatPrice(params.row.total.valuedUpToPrice, currency);
      },
    },
    {
      field: 'savings',
      headerName: 'Savings',
      flex: 1,
      renderCell: (params) => {
        return params.row.total?.discount > 0 ? (
          <Chip label={`${(params.row.total.discount * 100).toFixed(0)}% off`} color="secondary" size="small" />
        ) : (
          'No discount'
        );
      },
    },
    {
      field: 'margin',
      headerName: 'Margin',
      flex: 1,
      renderCell: (params) => {
        return formatMargin(params.row.total.margin);
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 1,
      renderCell: (params) => (
        <IconButton size="small" color="primary" onClick={() => handleRateDetailsClick(params.row)}>
          <InfoIcon />
        </IconButton>
      ),
    },
    {
      field: 'mergedRates',
      headerName: 'Merged',
      flex: 1,
      renderCell: (params) => {
        const mergedRates = groupedMergedRates[getMergedRateKeyForWinningRate(params.row)] ?? [];
        return <GroupedRates winningRate={params.row} mergedRates={mergedRates} currency={currency} />;
      },
    },
  ];

  return (
    <>
      {sortedRoomsWithRates.map(([roomId, room]) => (
        <Paper
          key={roomId}
          sx={{
            mb: 3,
            overflow: 'hidden',
            ...FULL_SCREEN_WIDTH_OVERRIDE_STYLES,
          }}
        >
          <Box sx={{ p: 2, bgcolor: theme.palette.grey[100] }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="column" alignItems="flex-start">
                <Typography variant="h6">{room.name}</Typography>
                <Typography variant="subtitle2">
                  ID: {roomId}
                  <IconButton title="Copy Room ID" onClick={() => handleCopyClick(roomId)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" onClick={() => handleFacilitiesClick(room.rates?.[0], room.name)}>
                View Facilities
              </Button>
            </Stack>
          </Box>

          {room.rates?.length > 0 && (
            <DataGrid
              rows={room.rates || []}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5 },
                },
              }}
              pageSizeOptions={[5, 10, 20]}
              disableColumnFilter
              disableColumnMenu
              disableDensitySelector
              disableColumnSelector
              hideFooterSelectedRowCount
            />
          )}
          {room.rates?.length === 0 && (
            <Box sx={{ p: 2, bgcolor: theme.palette.grey[100], textAlign: 'center' }}>
              <Typography variant="subtitle1">No rates found for this room</Typography>
            </Box>
          )}
        </Paper>
      ))}
    </>
  );
}
